import {createSlice, createAsyncThunk, PayloadAction} from "@reduxjs/toolkit";
import {
    ErrorStatusTypes,
    ErrorsStack,
    parseErrorMessage,
} from "components/common/errorsDeclarations";
import * as frazioniOdPApi from "api/schedulazione/frazioniOdPApi";
import {toast} from "react-toastify";
import {FiltriFrazioniOdP} from "components/userPreference/userPreferenceSlice";
import {deleteOdPLegacy} from "../produzione/odpLegacySlice";

// ============================================
// INTERFACES
// ============================================

export interface FaseFrazione {
    id?: number;
    frazione?: number; // FK FrazioneOdP
    fase_legacy?: number; // FK FaseLegacy
    fase_legacy_display?: string; // display dal backend
    quantita?: number;
    quantita_completata?: number;
    reparto?: number; // FK Reparto
    reparto_display?: string;
    operatore?: number; // FK Reparto
    operatore_display?: string;
    unita_misura?:string;
    codice_lavorazione?:string;
    descrizione?:string;
    data_inizio_pianificata?: string;
    data_fine_pianificata?: string;
    ore_previste?: number;
    ore_lavorate?: number;
    ore_residue?: number; // property backend
    stato?: number;
    stato_display?: string;
    ordinamento?: number;
    note?: string;
    percentuale_avanzamento?: number; // property backend
    ha_ostacoli_attivi?: boolean; // property backend
    is_bloccata?: boolean; // property backend
    errorsStack?: ErrorsStack;
}

export interface FrazioneOdP {
    id?: number;
    odp?: number; // FK OdPLegacy
    odp_display?: string; // display dal backend (es: "2024/00123")
    codice_articolo?: string; // display dal backend (es: "2024/00123")
    numero_frazione?: number;
    stato?: number;
    stato_display?: string;
    note?: string;
    percentuale_avanzamento?: number; // property backend
    ha_ostacoli_attivi?: boolean; // property backend
    fasi?: FaseFrazione[];
    errorsStack?: ErrorsStack;
}

export interface FrazioniOdPList {
    count: number;
    page: number;
    num_pages: number;
    next?: string;
    previous?: string;
    results: FrazioneOdP[];
    errorsStack: ErrorsStack;
}

// ============================================
// STATO SLICE
// ============================================

export interface FrazioniOdPState {
    frazioniOdPList: FrazioniOdPList;
    frazioneOdPCorrenteId?: number; // SOLO ID, mai oggetto completo
    // NO filtri qui! Sono in userPreferenceSlice
}

const initialState: FrazioniOdPState = {
    frazioniOdPList: {
        count: 0,
        page: 0,
        num_pages: 0,
        next: undefined,
        previous: undefined,
        results: [],
        errorsStack: {status: ErrorStatusTypes.OK},
    },
    frazioneOdPCorrenteId: undefined,
};

// ============================================
// ASYNC THUNKS
// ============================================

export const fetchFrazioniOdP = createAsyncThunk(
    "frazioniOdP/fetchFrazioniOdP",
    async (parametri: FiltriFrazioniOdP = {}) => {
        return await frazioniOdPApi.fetchFrazioniOdP(parametri);
    }
);

export const getFrazioneOdP = createAsyncThunk(
    "frazioniOdP/getFrazioneOdP",
    async (parametri: { frazioneOdPId: number }) => {
        return await frazioniOdPApi.getFrazioneOdP(parametri.frazioneOdPId);
    }
);

export const saveFrazioneOdP = createAsyncThunk(
    "frazioniOdP/saveFrazioneOdP",
    async (parametri: { frazioneOdPToSave: FrazioneOdP }) => {
        return await frazioniOdPApi.saveFrazioneOdP(parametri.frazioneOdPToSave);
    }
);

export const deleteFrazioneOdP = createAsyncThunk(
    "frazioniOdP/deleteFrazioneOdP",
    async (parametri: { frazioneOdPToDelete: FrazioneOdP }) => {
        return await frazioniOdPApi.deleteFrazioneOdP(parametri.frazioneOdPToDelete);
    }
);

// ============================================
// Gestione D&D
// ============================================


export const schedulaFrazione = createAsyncThunk(
    "frazioniOdP/schedulaFrazione",
    async (parametri: { odpId: number, data: string }) => {
        return await frazioniOdPApi.schedulaFrazione(parametri.odpId, parametri.data);
    }
);


export const rischedulaFrazione = createAsyncThunk(
    "frazioniOdP/rischedulaFrazione",
    async (parametri: { frazioneId: number; nuovaDataInizio: string }, thunkAPI) => {
        const result = await frazioniOdPApi.rischedulaFrazione(
            parametri.frazioneId,
            parametri.nuovaDataInizio
        );
        // Ricarica la frazione per avere i dati aggiornati
        thunkAPI.dispatch(getFrazioneOdP({frazioneOdPId: parametri.frazioneId}));
        return result;
    }
);

// ============================================
// ASYNC THUNKS - FASI FRAZIONE
// ============================================

export const saveFaseFrazione = createAsyncThunk(
    "frazioniOdP/saveFaseFrazione",
    async (
        parametri: {
            frazioneId: number;
            faseFrazioneToSave: FaseFrazione;
            azione?: string;
        },
        thunkAPI
    ) => {
        const result = await frazioniOdPApi.saveFaseFrazione(
            parametri.frazioneId,
            parametri.faseFrazioneToSave,
            parametri.azione
        );
        // Ricarica la frazione padre per avere i dati aggiornati (fasi nestate)
        thunkAPI.dispatch(getFrazioneOdP({frazioneOdPId: parametri.frazioneId}));
        return result;
    }
);

export const rischedulaFaseFrazione = createAsyncThunk(
    "frazioniOdP/rischedulaFaseFrazione",
    async (parametri: {
        faseFrazioneId: number;
        data_inizio_pianificata?: string;
        data_fine_pianificata?: string;
    }, thunkAPI) => {
        const result = await frazioniOdPApi.rischedulaFaseFrazione(
            parametri.faseFrazioneId,
            {
                data_inizio_pianificata: parametri.data_inizio_pianificata,
                data_fine_pianificata: parametri.data_fine_pianificata,
            }
        );
        // Ricarica la frazione padre per avere i dati aggiornati
        thunkAPI.dispatch(getFrazioneOdP({frazioneOdPId: result.frazione}));
        return result;
    }
);

export const deleteFaseFrazione = createAsyncThunk(
    "frazioniOdP/deleteFaseFrazione",
    async (
        parametri: {
            frazioneId: number;
            faseFrazioneToDelete: FaseFrazione;
        },
        thunkAPI
    ) => {
        const result = await frazioniOdPApi.deleteFaseFrazione(
            parametri.frazioneId,
            parametri.faseFrazioneToDelete
        );
        // Se era l'ultima fase, il BE cascade-elimina anche la frazione padre:
        // in quel caso saltare il refetch (genererebbe un 404).
        if (!result?.frazione_eliminata) {
            thunkAPI.dispatch(getFrazioneOdP({frazioneOdPId: parametri.frazioneId}));
        }
        return result;
    }
);

// ============================================
// SLICE
// ============================================

export const frazioniOdPSlice = createSlice({
    name: "frazioniOdP",
    initialState,
    reducers: {
        setFrazioneOdPCorrenteId: (state, action: PayloadAction<number | undefined>) => {
            state.frazioneOdPCorrenteId = action.payload;
        },
        resetFrazioniOdP: (state) => {
            state.frazioneOdPCorrenteId = undefined;
            state.frazioniOdPList = initialState.frazioniOdPList;
        },
    },
    extraReducers: (builder) => {
        // ============================================
        // FETCH (Lista)
        // ============================================
        builder.addCase(fetchFrazioniOdP.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(fetchFrazioniOdP.fulfilled, (state, action) => {
            state.frazioniOdPList = action.payload;
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(fetchFrazioniOdP.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // GET (Singolo con fasi nestate)
        // ============================================
        builder.addCase(getFrazioneOdP.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(getFrazioneOdP.fulfilled, (state, action) => {
            // Rimuove versione esistente
            state.frazioniOdPList.results = state.frazioniOdPList.results.filter(
                (frazione) => frazione.id !== action.payload.id
            );
            // Aggiunge versione aggiornata
            state.frazioniOdPList.results.push(action.payload);
            // Ordina per id
            state.frazioniOdPList.results.sort((a, b) => (a.id || 0) - (b.id || 0));

            state.frazioneOdPCorrenteId = action.payload.id;
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(getFrazioneOdP.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // SAVE (Create/Update) - NO TOAST, usa AutoSaveIndicator
        // ============================================
        builder.addCase(saveFrazioneOdP.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(saveFrazioneOdP.fulfilled, (state, action) => {
            const index = state.frazioniOdPList.results.findIndex(
                (frazione) => frazione.id === action.payload.id
            );

            if (index !== -1) {
                // Update esistente
                state.frazioniOdPList.results[index] = {
                    ...action.payload,
                    errorsStack: {status: ErrorStatusTypes.SUCCESS},
                };
            } else {
                // Nuova frazione
                state.frazioniOdPList.results.unshift({
                    ...action.payload,
                    errorsStack: {status: ErrorStatusTypes.SUCCESS},
                });
                state.frazioniOdPList.count++;
            }

            state.frazioniOdPList.results.sort((a, b) => (a.id || 0) - (b.id || 0));
            state.frazioneOdPCorrenteId = action.payload.id;
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.SUCCESS};
        });
        builder.addCase(saveFrazioneOdP.rejected, (state, action) => {
            // Errore specifico sull'elemento
            state.frazioniOdPList.results = state.frazioniOdPList.results.map((frazione) => {
                if (frazione.id === action.meta.arg.frazioneOdPToSave.id) {
                    return {
                        ...frazione,
                        errorsStack: parseErrorMessage(action.error),
                    };
                }
                return frazione;
            });
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // DELETE
        // ============================================
        builder.addCase(deleteFrazioneOdP.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(deleteFrazioneOdP.fulfilled, (state, action) => {
            state.frazioniOdPList.results = state.frazioniOdPList.results.filter(
                (frazione) => frazione.id !== action.meta.arg.frazioneOdPToDelete.id
            );
            state.frazioniOdPList.count--;
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.OK};

            toast.success("Frazione OdP eliminata con successo");
        });
        builder.addCase(deleteFrazioneOdP.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione della frazione");
        });

        // Pulizia store quando si smette di seguire un OdPLegacy:
        // il BE cancella in cascade le FrazioneOdP, qui rimuoviamo le orfane dalla cache.
        builder.addCase(deleteOdPLegacy.fulfilled, (state, action) => {
            const odpId = action.meta.arg.odpLegacyToDelete.id;
            if (odpId == null) return;
            const before = state.frazioniOdPList.results.length;
            state.frazioniOdPList.results = state.frazioniOdPList.results.filter(
                (frazione) => frazione.odp !== odpId
            );
            state.frazioniOdPList.count -= before - state.frazioniOdPList.results.length;
        });

        // ============================================
        // Gestione D&D
        // ============================================
        builder.addCase(schedulaFrazione.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(schedulaFrazione.fulfilled, (state, action) => {
            // Nuova frazione
            state.frazioniOdPList.results.unshift({
                ...action.payload,
                errorsStack: {status: ErrorStatusTypes.SUCCESS},
            });
            state.frazioniOdPList.count++;

            state.frazioniOdPList.results.sort((a, b) => (a.id || 0) - (b.id || 0));
            state.frazioneOdPCorrenteId = action.payload.id;
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.SUCCESS};
        });
        builder.addCase(schedulaFrazione.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // RISCHEDULA FRAZIONE (slittamento in blocco)
        // ============================================
        builder.addCase(rischedulaFrazione.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(rischedulaFrazione.fulfilled, (state) => {
            // Il refresh dei dati avviene tramite dispatch di getFrazioneOdP nel thunk
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(rischedulaFrazione.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nella rischedulazione della frazione");
        });

        // ============================================
        // RISCHEDULA FASE FRAZIONE (resize / spostamento)
        // Aggiorna la fase nella frazione padre senza ricaricare tutto
        // ============================================
        builder.addCase(rischedulaFaseFrazione.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(rischedulaFaseFrazione.fulfilled, (state) => {
            // Il refresh dei dati avviene tramite dispatch di getFrazioneOdP nel thunk
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.OK};
        });
        builder.addCase(rischedulaFaseFrazione.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nella rischedulazione della fase");
        });

        // ============================================
        // SAVE FASE FRAZIONE - NO TOAST, usa AutoSaveIndicator
        // Il refresh dati avviene tramite dispatch di getFrazioneOdP nel thunk
        // ============================================
        builder.addCase(saveFaseFrazione.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(saveFaseFrazione.fulfilled, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.SUCCESS};
        });
        builder.addCase(saveFaseFrazione.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
        });

        // ============================================
        // DELETE FASE FRAZIONE
        // Il refresh dati avviene tramite dispatch di getFrazioneOdP nel thunk
        // ============================================
        builder.addCase(deleteFaseFrazione.pending, (state) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.PENDING};
        });
        builder.addCase(deleteFaseFrazione.fulfilled, (state, action) => {
            state.frazioniOdPList.errorsStack = {status: ErrorStatusTypes.OK};
            // Se era l'ultima fase, il BE ha cascade-eliminato la frazione: pulisce la cache.
            if (action.payload?.frazione_eliminata) {
                const frazioneId = action.meta.arg.frazioneId;
                const before = state.frazioniOdPList.results.length;
                state.frazioniOdPList.results = state.frazioniOdPList.results.filter(
                    (frazione) => frazione.id !== frazioneId
                );
                state.frazioniOdPList.count -= before - state.frazioniOdPList.results.length;
            }
            toast.success("Fase eliminata con successo");
        });
        builder.addCase(deleteFaseFrazione.rejected, (state, action) => {
            state.frazioniOdPList.errorsStack = parseErrorMessage(action.error);
            toast.error("Errore nell'eliminazione della fase");
        });

    },
});

// ============================================
// EXPORTS
// ============================================

export const {
    setFrazioneOdPCorrenteId,
    resetFrazioniOdP,
} = frazioniOdPSlice.actions;

export const frazioniOdPSliceReducer = frazioniOdPSlice.reducer;